'use client'

import React, { useEffect, useMemo, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { Loader2, Plus, X, ArrowUp, ArrowDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate } from '../ProcessedEntry/DomainEntry/processedTypes'

export type ManageEntitySortField = 'name' | 'created_at' | 'updated_at'

const SORT_OPTIONS: { value: ManageEntitySortField; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Created' },
  { value: 'updated_at', label: 'Updated' },
]

export type ManageEntitySubmitResult = { success: boolean; id?: string | number; error?: string }

export function ManageEntityField({ label, theme, children }: { label: string; theme: ProcessedTheme; children: ReactNode }) {
  const c = watchClasses(theme)
  return (
    <div className="mb-3">
      <div className={`mb-1 text-[12px] font-semibold ${c.label}`}>{label}</div>
      {children}
    </div>
  )
}

export function ManageEntityView<T, F>({
  theme,
  title,
  createLabel,
  items,
  loading,
  getId,
  getName,
  getCode,
  getCreatedAt,
  getUpdatedAt,
  renderListMeta,
  emptyForm,
  formFromItem,
  renderFields,
  isFormValid,
  invalidMessage = 'Please fill in the required fields',
  onSubmit,
  onAfterSuccess,
}: {
  theme: ProcessedTheme
  title: string
  createLabel: string
  items: T[]
  loading: boolean
  getId: (item: T) => string | number
  getName: (item: T) => string
  getCode: (item: T) => string
  getCreatedAt?: (item: T) => string | null | undefined
  getUpdatedAt?: (item: T) => string | null | undefined
  renderListMeta?: (item: T) => ReactNode
  emptyForm: F
  formFromItem: (item: T) => F
  renderFields: (form: F, setForm: Dispatch<SetStateAction<F | undefined>>, mode: 'create' | 'edit') => ReactNode
  isFormValid: (form: F) => boolean
  invalidMessage?: string
  onSubmit: (form: F, mode: 'create' | 'edit', id: string | number | undefined) => Promise<ManageEntitySubmitResult>
  onAfterSuccess?: () => void
}) {
  const c = watchClasses(theme)

  const [query, setQuery] = useState('')
  const [sortField, setSortField] = useState<ManageEntitySortField>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [mode, setMode] = useState<'idle' | 'create' | 'edit'>('idle')
  const [selectedId, setSelectedId] = useState<string | number | undefined>()
  const [form, setForm] = useState<F | undefined>()
  const [status, setStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | undefined>()

  const sortableFields = useMemo(
    () => SORT_OPTIONS.filter((o) => o.value === 'name' || (o.value === 'created_at' && getCreatedAt) || (o.value === 'updated_at' && getUpdatedAt)),
    [getCreatedAt, getUpdatedAt],
  )

  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q ? items.filter((i) => getName(i).toLowerCase().includes(q) || getCode(i).toLowerCase().includes(q)) : items

    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      if (sortField === 'name') return getName(a).localeCompare(getName(b)) * dir
      const getter = sortField === 'created_at' ? getCreatedAt : getUpdatedAt
      const av = getter?.(a)
      const bv = getter?.(b)
      if (!av && !bv) return 0
      if (!av) return 1 // missing dates always sort to the end, regardless of direction
      if (!bv) return -1
      return (new Date(av).getTime() - new Date(bv).getTime()) * dir
    })
  }, [items, query, sortField, sortDir, getName, getCode, getCreatedAt, getUpdatedAt])

  const selectItem = (item: T) => {
    setMode('edit')
    setSelectedId(getId(item))
    setForm(formFromItem(item))
    setStatus('idle')
    setError(undefined)
  }

  const startCreate = () => {
    setMode('create')
    setSelectedId(undefined)
    setForm(emptyForm)
    setStatus('idle')
    setError(undefined)
  }

  const cancelForm = () => {
    setMode('idle')
    setSelectedId(undefined)
    setForm(undefined)
    setStatus('idle')
    setError(undefined)
  }

  // Keep the edit form in sync if the underlying list refreshes (e.g. after a save)
  useEffect(() => {
    if (mode !== 'edit' || selectedId === undefined) return
    const current = items.find((i) => getId(i) === selectedId)
    if (current) setForm(formFromItem(current))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, mode, selectedId])

  const submit = async () => {
    if (!form || mode === 'idle') return
    if (!isFormValid(form)) {
      setError(invalidMessage)
      return
    }
    setStatus('saving')
    setError(undefined)

    const result = await onSubmit(form, mode, selectedId)
    if (!result.success) {
      setStatus('error')
      setError(result.error ?? 'Failed to save')
      return
    }

    setStatus('success')
    onAfterSuccess?.()
    if (mode === 'create') {
      if (result.id !== undefined) {
        setMode('edit')
        setSelectedId(result.id)
      } else {
        setMode('idle')
        setForm(undefined)
      }
    }
  }

  const toggleSortDir = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))

  const metaLine = (item: T) => {
    const created = getCreatedAt?.(item)
    const updated = getUpdatedAt?.(item)
    if (!created && !updated) return null
    const parts: string[] = []
    if (created) parts.push(`Created ${compactDate(created)}`)
    if (updated) parts.push(`Updated ${compactDate(updated)}`)
    return <span className={`text-[9px] ${c.label}`}>{parts.join(' · ')}</span>
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden p-5">
      <div className="flex items-center justify-between">
        <h1 className={`text-lg font-bold ${c.text}`}>{title}</h1>
        {mode !== 'idle' ? (
          <button
            onClick={cancelForm}
            className={`flex items-center gap-1.5 rounded border px-3 py-1.5 text-[12px] font-semibold ${c.line} ${c.faint}`}
          >
            <X className="h-3.5 w-3.5" />
            Cancel
          </button>
        ) : (
          <button
            onClick={startCreate}
            className="flex items-center gap-1.5 rounded bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-signal"
          >
            <Plus className="h-3.5 w-3.5" />
            {createLabel}
          </button>
        )}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-[300px_1fr]">
        {/* List */}
        <div className={`flex flex-col overflow-hidden border ${c.panel} ${c.line}`}>
          <div className={`flex flex-col gap-2 border-b p-3 ${c.line}`}>
            <Input className={c.input} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`} />
            <div className="flex items-center gap-1.5">
              <select
                className={`flex-1 rounded border bg-transparent px-2 py-1 text-[11px] ${c.input}`}
                value={sortField}
                onChange={(e) => setSortField(e.target.value as ManageEntitySortField)}
              >
                {sortableFields.map((o) => (
                  <option key={o.value} value={o.value}>Sort: {o.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={toggleSortDir}
                title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                className={`flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded border ${c.line} ${c.faint}`}
              >
                {sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-signal" />
              </div>
            )}
            {!loading && visibleItems.length === 0 && (
              <div className={`px-3 py-4 text-[12px] ${c.label}`}>No {title.toLowerCase()} found</div>
            )}
            {visibleItems.map((item) => {
              const id = getId(item)
              return (
                <button
                  key={id}
                  onClick={() => selectItem(item)}
                  className={`flex w-full flex-col items-start gap-0.5 border-b px-3 py-2 text-left transition-colors ${c.line} ${
                    mode === 'edit' && selectedId === id ? 'bg-signal-surface' : 'hover:bg-secondary'
                  }`}
                >
                  <span className={`text-[13px] font-medium ${c.text}`}>{getName(item)}</span>
                  <span className="flex items-center gap-1.5">
                    <span className={`font-mono text-[10px] ${c.label}`}>{getCode(item)}</span>
                    {renderListMeta?.(item)}
                  </span>
                  {metaLine(item)}
                </button>
              )
            })}
          </div>
        </div>

        {/* Create / Edit form */}
        <div className={`overflow-y-auto border p-5 ${c.panel} ${c.line}`}>
          {mode === 'idle' && <div className={`text-[13px] ${c.label}`}>Select an item on the left to edit it, or click {createLabel} above.</div>}

          {form && mode !== 'idle' && (
            <>
              <h2 className={`mb-4 text-base font-bold ${c.text}`}>{mode === 'create' ? createLabel : `Edit ${title.slice(0, -1) || title}`}</h2>

              {renderFields(form, setForm, mode)}

              {error && <div className="mb-2 text-[12px] text-destructive">{error}</div>}
              {status === 'success' && <div className="mb-2 text-[12px] text-success">{mode === 'create' ? 'Created successfully' : 'Updated successfully'}</div>}

              <button
                onClick={submit}
                disabled={status === 'saving'}
                className="w-full rounded bg-primary text-primary-foreground py-2 text-[13px] font-semibold text-white hover:bg-signal disabled:opacity-50"
              >
                {status === 'saving' ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : mode === 'create' ? createLabel : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
